import { useCallback, useEffect, useRef } from "react";
import { App, Flex } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import errorCodes from "@/apis/error";
import { upcomingMaintain } from "@/apis/equipment";
import useDataList from "@/hooks/useDataList";
import STableLayout from "@/styles/STableLayout";
import { MaintainUpcoming } from "@/types/equipment";
import { useEquipmentEnumsContext } from "../../context/EquipmentEnumsContext";
import useMaintainUpcomingColumn from "../../hooks/useMaintainUpcomingColumn";

// 保养到期页负责加载当前组织的到期设备并展示本地分页表格。
const MaintainUpcomingPage = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  // 保存当前请求控制器，用于切换请求或卸载页面时主动取消。
  const controllerRef = useRef<AbortController>();
  const [items, , loading, { fetchList }] = useDataList<
    MaintainUpcoming & { id: string },
    Parameters<typeof upcomingMaintain>
  >({ fetchApi: upcomingMaintain });
  const { statusItems } = useEquipmentEnumsContext();

  const loadList = useCallback(async () => {
    // 新请求开始前取消旧请求，防止过期响应覆盖最新数据。
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      await fetchList(controller);
    } catch (error) {
      if (!controller.signal.aborted) {
        notification.error({
          message: t("equipment.upcomingMaintainFetchFailed"),
          description: errorCodes[Number(error)] ?? String(error),
        });
      }
    }
  }, [fetchList, notification, t]);

  // 页面卸载时终止尚未完成的请求。
  useEffect(() => {
    loadList();
    return () => controllerRef.current?.abort();
  }, [loadList]);

  const columns = useMaintainUpcomingColumn({ statusItems });

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        rowKey={item => item.equipment.id}
        loading={loading}
        dataSource={items}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={null}
        total={items.length}
        scrollX={3300}
      />
    </Flex>
  );
};

export default MaintainUpcomingPage;
